import React from 'react';
import {
  SparkAccordion,
  SparkDrawer,
  SparkImageLoader,
  SparkTabs,
} from 'sharedComponents/UI';
import { Close } from '@mui/icons-material';
import { Box, Grid, Icon, IconButton, Typography } from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import { Image } from 'lucide-react';
import { TabItem } from 'sharedComponents/UI/SparkTabs';

type ChatsPluginDrawerProps = {
  open: boolean;
  onClose: () => void;
  chatMessagePlugins: any[];
  top?: string;
  resizable?: boolean;
  drawerHeight?: string;
  openWidth?: number;
};

function ChatsPluginDrawer({
  open,
  onClose,
  chatMessagePlugins,
  top = '4rem',
  resizable = false,
  drawerHeight = '90vh',
  openWidth = 500,
}: ChatsPluginDrawerProps) {
  const tabItems: TabItem[] = React.useMemo(() => {
    return [
      {
        label: 'Event',
        content: (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CheckIcon
                sx={{
                  mr: 1,
                  padding: 0.5,
                  border: '1px solid #eeeeee',
                  backgroundColor: '#eeeeee',
                  borderRadius: '100%',
                  width: 16,
                  height: 16,
                }}
                fontWeight={900}
              />
              <Typography>Success</Typography>
            </Box>
            <Box>
              <Typography variant='body1' fontWeight={800}>
                Event ID
              </Typography>
              <Typography variant='body2'>123</Typography>
            </Box>
            <Box>
              <Typography variant='body1' fontWeight={800}>
                Event Date
              </Typography>
              <Typography variant='body2'>2025-01-01 12:00</Typography>
            </Box>
            <Box>
              <Typography variant='body1' fontWeight={800}>
                Message
              </Typography>
              <Typography variant='body2'>OK</Typography>
            </Box>
          </Box>
        ),
      },

      {
        label: 'Input',
        content: (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: 3,
            }}
          >
            <Box>
              <Typography variant='body2'>{`"{"lat": 37.7749, "lng": -122.4194}"`}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        label: 'Output',
        content: <></>,
      },
      {
        label: 'Filter',
        content: (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CheckIcon
                sx={{
                  mr: 1,
                  padding: 0.5,
                  border: '1px solid #eeeeee',
                  backgroundColor: '#eeeeee',
                  borderRadius: '100%',
                  width: 16,
                  height: 16,
                }}
                fontWeight={900}
              />
              <Typography>Run</Typography>
            </Box>
            <Box>
              <Typography variant='body1' fontWeight={800}>
                Filter ID
              </Typography>
              <Typography variant='body2'>123</Typography>
            </Box>
            <Box>
              <Typography variant='body1' fontWeight={800}>
                Filter Date
              </Typography>
              <Typography variant='body2'>2025-01-01 12:00</Typography>
            </Box>
            <Box>
              <Typography variant='body1' fontWeight={800}>
                User
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Image width={20} height={20} />
                <Typography variant='body2'>Jaco Gerrits</Typography>
              </Box>
            </Box>
          </Box>
        ),
      },
    ];
  }, []);

  return (
    <SparkDrawer
      anchor='right'
      variant='permanent'
      open={open}
      top={top}
      openWidth={openWidth}
      resizable={resizable}
      drawerHeight={drawerHeight}
    >
      <Box>
        <Grid
          container
          height={'3rem'}
          alignItems='center'
          borderBottom={'1px solid #eeeeee'}
        >
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='nowrap'
            width='100%'
            px={2}
          >
            <Box
              sx={{
                fontFamily: 'Mont-semibold',
                fontSize: 24,
                maxWidth: 'inherit',
                paddingRight: '5px',
              }}
            >
              <Typography variant='body1'>Plugins</Typography>
            </Box>
            <IconButton
              aria-label='close'
              onClick={onClose}
              size='small'
              sx={{
                marginRight: -1,
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </Grid>
        {[1, 2, 3].map((item, index) => (
          <Box key={index} my={1} mx={2} bgcolor='white' borderRadius='8px'>
            <SparkAccordion
              title={
                <Box display='flex' alignItems='center'>
                  <CheckIcon
                    sx={{
                      mr: 1,
                      padding: 0.5,
                      border: '1px solid #eeeeee',
                      borderRadius: '100%',
                      width: 12,
                      height: 12,
                    }}
                    fontWeight={900}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <SparkImageLoader
                      src={''}
                      width='40px'
                      height='45px'
                      style={{
                        borderRadius: '10px',
                        objectFit: 'contain',
                        padding: '1px',
                      }}
                      alt={'title'}
                      fallback={
                        <Icon
                          sx={{
                            width: '40px',
                            height: '45px',
                            '& svg': {
                              height: 'inherit',
                            },
                          }}
                        >
                          <Image width={'40px'} height={'45px'} />
                        </Icon>
                      }
                    />
                    <Box>
                      <Typography variant='h6'>Sparkup</Typography>
                      <Typography variant='caption' color={'#BDBDBD'}>
                        2025-01-01 12:00
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              summaryStyles={{
                cursor: 'pointer',
                p: 1,
              }}
              collapseProps={{ sx: { px: 1 } }}
              expandedSx={{
                '&:hover': {
                  border: '1px solid #6267ac',
                },
              }}
              sx={{
                border: '1px solid #E0E0E0',
                '&:hover': {
                  border: '1px solid #6267ac',
                },
              }}
            >
              <Box px={1}>
                <Typography variant='subtitle1' color={'#BDBDBD'}>
                  Sends an email to a user
                </Typography>
                <SparkTabs tabItems={tabItems} tabPanelStyles={{ py: 2 }} />
              </Box>
            </SparkAccordion>
          </Box>
        ))}
      </Box>
    </SparkDrawer>
  );
}

export default ChatsPluginDrawer;
